import React, { useMemo, useCallback, useRef, useState } from 'react';
import { Component } from '@/types';
import { shouldBlockSensorControl, useComponentInteraction } from '@/components/shared/rendering/InteractionContextRuntime';
import { useDataMultiValue } from '@/hooks/useDataMultiValue';
import { ChartDataSource } from '@/types/chartDataSource';
import { CONTROL_THEME } from './controlThemeTokens';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ThermostatProps {
  component: Component;
}

interface ThermostatTheme {
  trackColor: string;
  coolColor: string;
  heatColor: string;
  autoColor: string;
  offColor: string;
  backgroundColor: string;
  borderColor: string;
  temperatureColor: string;
  targetColor: string;
  humidityColor: string;
  modeSelectedBg: string;
  modeSelectedText: string;
  modeUnselectedBg: string;
  modeUnselectedText: string;
  glowColor: string;
  glowIntensity: number;
  arcThickness: number;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const VIEW_SIZE = 220;
const CX = 110;
const CY = 110;
const OUTER_R = 95;
const ARC_START_ANGLE = 135;
const ARC_END_ANGLE = 405;
const DEFAULT_MODES = ['auto', 'heat', 'cool', 'fan', 'off'];
const CHART_DATA_SOURCE_TYPES = new Set([
  'static',
  'query-template',
  'timestamp-aggregate',
  'workflow',
  'composite-query',
  'home-assistant-placeholder',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getStringValue(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function getNumberValue(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function getBooleanValue(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function getStringArrayValue(value: unknown): string[] | undefined {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
    ? value
    : undefined;
}

function isChartDataSource(value: unknown): value is ChartDataSource {
  if (!isRecord(value)) {
    return false;
  }

  const type = getStringValue(value.type);
  return type != null && CHART_DATA_SOURCE_TYPES.has(type);
}

function getTemperatureUnit(value: unknown): 'celsius' | 'fahrenheit' | undefined {
  return value === 'celsius' || value === 'fahrenheit' ? value : undefined;
}

// ─── SVG Geometry (from RadialDial) ──────────────────────────────────────────

function polarToCartesian(
  cx: number, cy: number, r: number, angleDeg: number,
): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

function describeAnnularArc(
  cx: number, cy: number, innerR: number, outerR: number,
  startAngle: number, endAngle: number,
): string {
  const sweep = endAngle - startAngle;
  if (Math.abs(sweep) < 0.1) return '';
  const largeArc = Math.abs(sweep) > 180 ? 1 : 0;

  const outerStart = polarToCartesian(cx, cy, outerR, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerR, endAngle);
  const innerEnd = polarToCartesian(cx, cy, innerR, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerR, startAngle);

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

// ─── Temperature Utilities ───────────────────────────────────────────────────

function formatTemp(value: number, unit: 'celsius' | 'fahrenheit'): string {
  return `${Math.round(value)}°${unit === 'celsius' ? 'C' : 'F'}`;
}

// ─── Theme Presets ───────────────────────────────────────────────────────────

const THERMOSTAT_THEMES: Record<string, ThermostatTheme> = {
  'nest': {
    trackColor: CONTROL_THEME.muted,
    coolColor: '#4fc3f7',
    heatColor: '#ff6b35',
    autoColor: '#66bb6a',
    offColor: CONTROL_THEME.mutedForeground,
    backgroundColor: CONTROL_THEME.card,
    borderColor: CONTROL_THEME.border,
    temperatureColor: CONTROL_THEME.foreground,
    targetColor: CONTROL_THEME.mutedForeground,
    humidityColor: '#4fc3f7',
    modeSelectedBg: CONTROL_THEME.muted,
    modeSelectedText: CONTROL_THEME.foreground,
    modeUnselectedBg: 'transparent',
    modeUnselectedText: CONTROL_THEME.mutedForeground,
    glowColor: '#ff6b35',
    glowIntensity: 0,
    arcThickness: 12,
  },
  'minimal': {
    trackColor: CONTROL_THEME.muted,
    coolColor: '#42a5f5',
    heatColor: '#ef5350',
    autoColor: '#66bb6a',
    offColor: CONTROL_THEME.mutedForeground,
    backgroundColor: CONTROL_THEME.card,
    borderColor: CONTROL_THEME.border,
    temperatureColor: CONTROL_THEME.foreground,
    targetColor: CONTROL_THEME.mutedForeground,
    humidityColor: '#42a5f5',
    modeSelectedBg: CONTROL_THEME.muted,
    modeSelectedText: CONTROL_THEME.foreground,
    modeUnselectedBg: 'transparent',
    modeUnselectedText: CONTROL_THEME.mutedForeground,
    glowColor: 'transparent',
    glowIntensity: 0,
    arcThickness: 8,
  },
  'neon': {
    trackColor: '#1a1a2e',
    coolColor: '#00e5ff',
    heatColor: '#ff1744',
    autoColor: '#76ff03',
    offColor: '#37474f',
    backgroundColor: '#0d0d1a',
    borderColor: '#00e5ff33',
    temperatureColor: '#e0e0e0',
    targetColor: '#7c7c7c',
    humidityColor: '#00e5ff',
    modeSelectedBg: 'rgba(0, 229, 255, 0.15)',
    modeSelectedText: '#00e5ff',
    modeUnselectedBg: 'transparent',
    modeUnselectedText: '#555555',
    glowColor: '#00e5ff',
    glowIntensity: 0.6,
    arcThickness: 10,
  },
  'industrial': {
    trackColor: '#3e3e3e',
    coolColor: '#29b6f6',
    heatColor: '#ff8a65',
    autoColor: '#81c784',
    offColor: '#616161',
    backgroundColor: '#2c2c2c',
    borderColor: CONTROL_THEME.border,
    temperatureColor: '#ffd54f',
    targetColor: '#bdbdbd',
    humidityColor: '#29b6f6',
    modeSelectedBg: '#424242',
    modeSelectedText: '#ffd54f',
    modeUnselectedBg: 'transparent',
    modeUnselectedText: '#888888',
    glowColor: '#ffd54f',
    glowIntensity: 0.2,
    arcThickness: 14,
  },
  'retro': {
    trackColor: '#4a3728',
    coolColor: '#5dade2',
    heatColor: '#e67e22',
    autoColor: '#2ecc71',
    offColor: '#7f8c8d',
    backgroundColor: '#3b2d1e',
    borderColor: '#5d4e37',
    temperatureColor: '#f39c12',
    targetColor: '#bdc3c7',
    humidityColor: '#5dade2',
    modeSelectedBg: '#5d4e37',
    modeSelectedText: '#f39c12',
    modeUnselectedBg: 'transparent',
    modeUnselectedText: '#8d7b68',
    glowColor: '#f39c12',
    glowIntensity: 0.15,
    arcThickness: 16,
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

export const Thermostat: React.FC<ThermostatProps> = ({ component }) => {
  const { properties = {} } = component;
  const svgRef = useRef<SVGSVGElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localTarget, setLocalTarget] = useState<number | null>(null);

  // ─── Extract Properties ──────────────────────────────────────────────────

  const effectiveMin = getNumberValue(properties.min) ?? 50;
  const effectiveMax = getNumberValue(properties.max) ?? 90;
  const effectiveUnit = getTemperatureUnit(properties.unit) ?? 'fahrenheit';
  const themeName = getStringValue(properties.theme) ?? 'nest';
  const currentMode = getStringValue(properties.mode) ?? 'auto';
  const availableModes = getStringArrayValue(properties.modes) ?? DEFAULT_MODES;
  const showHumidity = getBooleanValue(properties.showHumidity) ?? true;
  const targetTemperature = getNumberValue(properties.targetTemperature) ?? 72;

  // ─── Theme Resolution ────────────────────────────────────────────────────

  const theme = useMemo(() => {
    const base = THERMOSTAT_THEMES[themeName] ?? THERMOSTAT_THEMES['nest'];
    return {
      ...base,
      trackColor: getStringValue(properties.trackColor) ?? base.trackColor,
      coolColor: getStringValue(properties.coolColor) ?? base.coolColor,
      heatColor: getStringValue(properties.heatColor) ?? base.heatColor,
      autoColor: getStringValue(properties.autoColor) ?? base.autoColor,
      offColor: getStringValue(properties.offColor) ?? base.offColor,
      backgroundColor: getStringValue(properties.backgroundColor) ?? base.backgroundColor,
      borderColor: getStringValue(properties.borderColor) ?? base.borderColor,
      temperatureColor: getStringValue(properties.temperatureColor) ?? base.temperatureColor,
      targetColor: getStringValue(properties.targetColor) ?? base.targetColor,
      humidityColor: getStringValue(properties.humidityColor) ?? base.humidityColor,
      modeSelectedBg: getStringValue(properties.modeSelectedBg) ?? base.modeSelectedBg,
      modeSelectedText: getStringValue(properties.modeSelectedText) ?? base.modeSelectedText,
      modeUnselectedBg: getStringValue(properties.modeUnselectedBg) ?? base.modeUnselectedBg,
      modeUnselectedText: getStringValue(properties.modeUnselectedText) ?? base.modeUnselectedText,
      glowColor: getStringValue(properties.glowColor) ?? base.glowColor,
      glowIntensity: getNumberValue(properties.glowIntensity) ?? base.glowIntensity,
      arcThickness: getNumberValue(properties.arcThickness) ?? base.arcThickness,
    };
  }, [themeName, properties.trackColor, properties.coolColor, properties.heatColor,
      properties.autoColor, properties.offColor, properties.backgroundColor,
      properties.borderColor, properties.temperatureColor, properties.targetColor, properties.humidityColor,
      properties.modeSelectedBg, properties.modeSelectedText, properties.modeUnselectedBg,
      properties.modeUnselectedText, properties.glowColor, properties.glowIntensity,
      properties.arcThickness]);

  // ─── Data Binding (dual-field via useDataMultiValue) ─────────────────────

  const dataSource = isChartDataSource(properties.dataSource) ? properties.dataSource : undefined;
  const templateId = getStringValue(properties.queryTemplateId);

  const {
    values: dataValues,
    isLoading,
  } = useDataMultiValue({
    dataSource,
    componentId: component.id,
    mode: 'field',
    fields: [
      {
        field: getStringValue(properties.temperatureField) ?? 'temperature',
        label: 'Temperature',
        color: theme.heatColor,
        max: effectiveMax,
      },
      {
        field: getStringValue(properties.humidityField) ?? 'humidity',
        label: 'Humidity',
        color: theme.humidityColor,
        max: 100,
      },
    ],
    staticValues: [
      {
        value: getNumberValue(properties.temperature) ?? 72,
        label: 'Temperature',
        color: theme.heatColor,
        max: effectiveMax,
      },
      {
        value: getNumberValue(properties.humidity) ?? 45,
        label: 'Humidity',
        color: theme.humidityColor,
        max: 100,
      },
    ],
    templateId,
    enabled: true,
  });

  const currentTemperature = dataValues[0]?.value ?? getNumberValue(properties.temperature) ?? 72;
  const currentHumidity = dataValues[1]?.value ?? getNumberValue(properties.humidity) ?? 45;

  // ─── Interaction ─────────────────────────────────────────────────────────

  const { handlers, mode } = useComponentInteraction(component);
  const isViewerMode = mode === 'functional';
  const sensorControlBlocked = shouldBlockSensorControl(component);
  const canControl = !sensorControlBlocked && Boolean(isViewerMode && handlers.onSensorControl);

  // ─── Computed Display Values ─────────────────────────────────────────────

  const displayTemperature = currentTemperature;
  const displayTarget = localTarget ?? targetTemperature;

  // ─── Arc Geometry ────────────────────────────────────────────────────────

  const totalSweep = ARC_END_ANGLE - ARC_START_ANGLE;
  const outerR = OUTER_R;
  const innerR = outerR - theme.arcThickness;

  const targetRatio = effectiveMin === effectiveMax
    ? 0
    : Math.max(0, Math.min(1, (displayTarget - effectiveMin) / (effectiveMax - effectiveMin)));
  const targetAngle = ARC_START_ANGLE + targetRatio * totalSweep;

  const trackPath = useMemo(
    () => describeAnnularArc(CX, CY, innerR, outerR, ARC_START_ANGLE, ARC_END_ANGLE),
    [innerR, outerR],
  );

  const targetPath = useMemo(
    () => targetRatio > 0.001
      ? describeAnnularArc(CX, CY, innerR, outerR, ARC_START_ANGLE, targetAngle)
      : '',
    [targetRatio, innerR, outerR, targetAngle],
  );

  const knobPos = useMemo(
    () => polarToCartesian(CX, CY, (outerR + innerR) / 2, targetAngle),
    [outerR, innerR, targetAngle],
  );

  const arcFillColor = useMemo(() => {
    switch (currentMode) {
      case 'heat': return theme.heatColor;
      case 'cool': return theme.coolColor;
      case 'auto': return theme.autoColor;
      case 'off':
      case 'fan':
      default: return theme.offColor;
    }
  }, [currentMode, theme]);

  // ─── Min/Max Label Positions ─────────────────────────────────────────────

  const minLabelPos = polarToCartesian(CX, CY, outerR + 12, ARC_START_ANGLE);
  const maxLabelPos = polarToCartesian(CX, CY, outerR + 12, ARC_END_ANGLE);

  // ─── Arc Drag Interaction ────────────────────────────────────────────────

  const getValueFromPointer = useCallback((clientX: number, clientY: number): number | null => {
    const svg = svgRef.current;
    if (!svg) return null;

    const rect = svg.getBoundingClientRect();
    const svgX = ((clientX - rect.left) / rect.width) * VIEW_SIZE;
    const svgY = ((clientY - rect.top) / rect.height) * VIEW_SIZE;

    const dx = svgX - CX;
    const dy = svgY - CY;
    let angle = Math.atan2(dy, dx) * (180 / Math.PI);
    if (angle < 0) angle += 360;

    let mapped = angle;
    if (mapped < ARC_START_ANGLE) mapped += 360;
    if (mapped < ARC_START_ANGLE || mapped > ARC_END_ANGLE) return null;

    const ratio = (mapped - ARC_START_ANGLE) / (ARC_END_ANGLE - ARC_START_ANGLE);
    const rawValue = effectiveMin + ratio * (effectiveMax - effectiveMin);
    return Math.round(rawValue);
  }, [effectiveMin, effectiveMax]);

  const handlePointerDown = useCallback((e: React.PointerEvent<SVGElement>) => {
    if (!canControl) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    if (e.target instanceof Element) {
      e.target.setPointerCapture?.(e.pointerId);
    }
    const value = getValueFromPointer(e.clientX, e.clientY);
    if (value !== null) setLocalTarget(value);
  }, [canControl, getValueFromPointer]);

  const handlePointerMove = useCallback((e: React.PointerEvent<SVGElement>) => {
    if (!isDragging) return;
    const value = getValueFromPointer(e.clientX, e.clientY);
    if (value !== null) setLocalTarget(value);
  }, [isDragging, getValueFromPointer]);

  const handlePointerUp = useCallback(() => {
    if (isDragging && localTarget !== null && handlers.onSensorControl) {
      handlers.onSensorControl({
        sensorId: component.id,
        action: 'control',
        parameters: { command: 'set', value: localTarget, property: 'targetTemperature' },
      });
    }
    setIsDragging(false);
  }, [isDragging, localTarget, handlers, component.id]);

  // ─── Up/Down Buttons ─────────────────────────────────────────────────────

  const handleTargetUp = useCallback(() => {
    if (!canControl || !handlers.onSensorControl) return;
    const newTarget = Math.min(effectiveMax, (localTarget ?? targetTemperature) + 1);
    setLocalTarget(newTarget);
    handlers.onSensorControl({
      sensorId: component.id,
      action: 'control',
      parameters: { command: 'set', value: newTarget, property: 'targetTemperature' },
    });
  }, [canControl, effectiveMax, localTarget, targetTemperature, handlers, component.id]);

  const handleTargetDown = useCallback(() => {
    if (!canControl || !handlers.onSensorControl) return;
    const newTarget = Math.max(effectiveMin, (localTarget ?? targetTemperature) - 1);
    setLocalTarget(newTarget);
    handlers.onSensorControl({
      sensorId: component.id,
      action: 'control',
      parameters: { command: 'set', value: newTarget, property: 'targetTemperature' },
    });
  }, [canControl, effectiveMin, localTarget, targetTemperature, handlers, component.id]);

  // ─── Mode Selection ──────────────────────────────────────────────────────

  const handleModeChange = useCallback((newMode: string) => {
    if (!canControl || !handlers.onSensorControl) return;
    handlers.onSensorControl({
      sensorId: component.id,
      action: 'control',
      parameters: { command: 'set', value: newMode, property: 'mode' },
    });
  }, [canControl, handlers, component.id]);

  // ─── Loading State ───────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div style={{
        width: '100%', height: '100%', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        backgroundColor: theme.backgroundColor, borderRadius: '16px',
      }}>
        <svg viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`} style={{ width: '80%', maxWidth: VIEW_SIZE }}>
          <circle cx={CX} cy={CY} r={outerR - 6} fill="none" stroke={theme.trackColor}
            strokeWidth={12} strokeDasharray="12 8" opacity={0.5}>
            <animateTransform attributeName="transform" type="rotate"
              from={`0 ${CX} ${CY}`} to={`360 ${CX} ${CY}`} dur="2s" repeatCount="indefinite" />
          </circle>
        </svg>
      </div>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div
      role="group"
      aria-label={`Thermostat: ${formatTemp(displayTemperature, effectiveUnit)}, target ${formatTemp(displayTarget, effectiveUnit)}, mode ${currentMode}`}
      data-testid="thermostat"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.backgroundColor,
        border: `1px solid ${theme.borderColor}`,
        borderRadius: '16px',
        overflow: 'hidden',
        userSelect: 'none',
        position: 'relative',
      }}
    >
      {/* Hidden range input for screen readers */}
      <input
        type="range"
        role="slider"
        aria-label="Target temperature"
        aria-valuemin={effectiveMin}
        aria-valuemax={effectiveMax}
        aria-valuenow={displayTarget}
        aria-valuetext={formatTemp(displayTarget, effectiveUnit)}
        value={displayTarget}
        min={effectiveMin}
        max={effectiveMax}
        step={1}
        disabled={!canControl}
        aria-disabled={!canControl}
        onChange={(e) => {
          if (!canControl || !handlers.onSensorControl) return;
          const v = Number(e.target.value);
          setLocalTarget(v);
          handlers.onSensorControl({
            sensorId: component.id,
            action: 'control',
            parameters: { command: 'set', value: v, property: 'targetTemperature' },
          });
        }}
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
        }}
      />

      {/* SVG Arc + Center Display */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`}
        style={{ width: '100%', maxWidth: VIEW_SIZE, flex: '1 1 auto' }}
      >
        {/* Glow filter */}
        {theme.glowIntensity > 0 && (
          <defs>
            <filter id={`thermo-glow-${component.id}`} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation={4 * theme.glowIntensity} />
            </filter>
          </defs>
        )}

        {/* Background track */}
        <path d={trackPath} fill={theme.trackColor} />

        {/* Glow layer */}
        {theme.glowIntensity > 0 && targetPath && (
          <path
            d={targetPath}
            fill={arcFillColor}
            opacity={theme.glowIntensity}
            filter={`url(#thermo-glow-${component.id})`}
          />
        )}

        {/* Filled arc to target */}
        {targetPath && <path d={targetPath} fill={arcFillColor} />}

        {/* Knob at target position */}
        <circle
          cx={knobPos.x}
          cy={knobPos.y}
          r={6}
          fill="#ffffff"
          stroke={arcFillColor}
          strokeWidth={2}
        />

        {/* Current temperature — large center text */}
        <text
          x={CX}
          y={CY - 8}
          textAnchor="middle"
          dominantBaseline="central"
          fill={theme.temperatureColor}
          fontSize="36"
          fontWeight="300"
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          {formatTemp(displayTemperature, effectiveUnit)}
        </text>

        {/* Target temperature — smaller text below */}
        <text
          x={CX}
          y={CY + 16}
          textAnchor="middle"
          dominantBaseline="central"
          fill={theme.targetColor}
          fontSize="12"
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          {`Target: ${formatTemp(displayTarget, effectiveUnit)}`}
        </text>

        {/* Humidity — optional */}
        {showHumidity && (
          <text
            x={CX}
            y={CY + 32}
            textAnchor="middle"
            dominantBaseline="central"
            fill={theme.humidityColor}
            fontSize="11"
            fontFamily="system-ui, -apple-system, sans-serif"
          >
            {`${Math.round(currentHumidity)}% humidity`}
          </text>
        )}

        {/* Up button */}
        <polygon
          points={`${CX},${CY - 38} ${CX - 8},${CY - 30} ${CX + 8},${CY - 30}`}
          fill={canControl ? theme.targetColor : '#555'}
          style={{ cursor: canControl ? 'pointer' : 'default' }}
          onClick={handleTargetUp}
          role="button"
          aria-label="Increase target temperature"
        />
        {/* Down button */}
        <polygon
          points={`${CX},${CY + 48} ${CX - 8},${CY + 40} ${CX + 8},${CY + 40}`}
          fill={canControl ? theme.targetColor : '#555'}
          style={{ cursor: canControl ? 'pointer' : 'default' }}
          onClick={handleTargetDown}
          role="button"
          aria-label="Decrease target temperature"
        />

        {/* Min label */}
        <text
          x={minLabelPos.x}
          y={minLabelPos.y}
          textAnchor="middle"
          dominantBaseline="central"
          fill={theme.targetColor}
          fontSize="9"
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          {effectiveMin}
        </text>

        {/* Max label */}
        <text
          x={maxLabelPos.x}
          y={maxLabelPos.y}
          textAnchor="middle"
          dominantBaseline="central"
          fill={theme.targetColor}
          fontSize="9"
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          {effectiveMax}
        </text>

        {/* Invisible interaction overlay */}
        {canControl && (
          <path
            d={describeAnnularArc(
              CX, CY,
              Math.max(0, innerR - 10),
              Math.min(VIEW_SIZE / 2, outerR + 10),
              ARC_START_ANGLE, ARC_END_ANGLE,
            )}
            fill="transparent"
            style={{ cursor: isDragging ? 'grabbing' : 'pointer' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          />
        )}
      </svg>

      {/* Mode selector pills */}
      <div
        role="tablist"
        aria-label="HVAC mode"
        style={{
          display: 'flex',
          gap: '4px',
          justifyContent: 'center',
          width: '100%',
          padding: '6px 8px 8px',
          boxSizing: 'border-box',
          flexShrink: 0,
        }}
      >
        {availableModes.map((m) => {
          const isSelected = m === currentMode;
          return (
            <button
              key={m}
              role="tab"
              aria-selected={isSelected}
              aria-label={`${m} mode`}
              tabIndex={canControl ? 0 : -1}
              onClick={() => handleModeChange(m)}
              style={{
                flex: '1 1 0',
                minWidth: 0,
                padding: '4px 6px',
                borderRadius: '12px',
                border: 'none',
                fontSize: '11px',
                fontWeight: isSelected ? 600 : 400,
                textTransform: 'capitalize',
                backgroundColor: isSelected ? theme.modeSelectedBg : theme.modeUnselectedBg,
                color: isSelected ? theme.modeSelectedText : theme.modeUnselectedText,
                cursor: canControl ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {m}
            </button>
          );
        })}
      </div>
    </div>
  );
};
